import React, { PropTypes } from 'react';
import { FormattedMessage } from 'react-intl';
import ImmutablePropTypes from 'react-immutable-proptypes';

import BcReactComponent from '@bibliocommons/base-bc-react-component';
import { TextButton } from '@bibliocommons/base-buttons';
import FullScreenOverlay from '@bibliocommons/base-full-screen-overlay';
import { Arrows, Comment, Quotation, Tag, Plus, Video } from '@bibliocommons/base-icons';
import { Dropdown } from '@bibliocommons/base-dropdown';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';
import { UGC_CONTENT_TYPES } from '@bibliocommons/constants-ugc';
import { supportsUGCContentType } from '@bibliocommons/utils-ugc';

import {
  bibShape,
  shelfItemShape,
  userIdentityShape,
  ugcCommentShape,
  ugcTagShape,
  ugcVideoShape,
  ugcPrivateNoteShape
} from '@bibliocommons/bc-prop-types';

import SimilarTitlesUGCEditor from '../../../shared/UGC/UGCEditors/SimilarTitlesUGCEditor';
import CommentUGCEditor from '../../../shared/UGC/UGCEditors/CommentUGCEditor';
import QuotationUGCEditor from '../../../shared/UGC/UGCEditors/QuotationUGCEditor';
import TagsUGCEditor from '../../../shared/UGC/UGCEditors/TagsUGCEditor';
import VideoUGCEditor from '../../../shared/UGC/UGCEditors/VideoUGCEditor';
import PrivateNoteUGCEditor from '../../../shared/UGC/UGCEditors/PrivateNoteUGCEditor';
import { AddTitleOverlay } from '../../../shared/AddTitle';

import './Toolbar.scss';

export default class ShelfListItemToolbar extends BcReactComponent {
  constructor(props) {
    super(props);
    this.bindAll('openUGCEditor', 'closeUGCEditor');

    this.state = {
      ugcContentType: null
    };
  }

  openUGCEditor(ugcContentType) {
    this.setState({ ugcContentType });
  }

  closeUGCEditor() {
    this.setState({ ugcContentType: null });
  }

  supportsUGCContentType(type) {
    return supportsUGCContentType(type, this.props.currentLibrary, this.props.localBranch);
  }

  renderCommentButton() {
    if (this.props.ownerCommentForBib || !this.supportsUGCContentType(UGC_CONTENT_TYPES.COMMENT)) {
      return null;
    }

    return (
      <TextButton className="shelf-ugc-comment-button" handleClick={() => this.openUGCEditor(UGC_CONTENT_TYPES.COMMENT)}>
        <span aria-hidden>
          <Comment className="shelf-ugc-icon" />
          <FormattedMessage id="ugc_toolbar_comment_btn_label" />
        </span>
        <ScreenReaderMessage>
          <FormattedMessage id="ada_add_comment" values={{ title: this.props.bib.getIn(['briefInfo', 'title']) }} />
        </ScreenReaderMessage>
      </TextButton>
    );
  }

  renderTagsButton() {
    if (!this.supportsUGCContentType(UGC_CONTENT_TYPES.TAGS)) {
      return null;
    }

    return (
      <TextButton className="shelf-ugc-tags-button" handleClick={() => this.openUGCEditor(UGC_CONTENT_TYPES.TAGS)}>
        <span aria-hidden>
          <Tag color="currentColor" className="shelf-ugc-icon" />
          <FormattedMessage id="ugc_toolbar_tags_btn_label" />
        </span>
        <ScreenReaderMessage>
          <FormattedMessage id="ada_add_tags" values={{ title: this.props.bib.getIn(['briefInfo', 'title']) }} />
        </ScreenReaderMessage>
      </TextButton>
    );
  }

  renderQuotationButton() {
    if (!this.supportsUGCContentType(UGC_CONTENT_TYPES.QUOTATIONS)) {
      return null;
    }

    return (
      <TextButton className="shelf-ugc-quotation-button" handleClick={() => this.openUGCEditor(UGC_CONTENT_TYPES.QUOTATIONS)}>
        <span aria-hidden>
          <Quotation className="shelf-ugc-icon" />
          <FormattedMessage id="ugc_toolbar_quotations_btn_label" />
        </span>
        <ScreenReaderMessage>
          <FormattedMessage id="ada_add_quotations" values={{ title: this.props.bib.getIn(['briefInfo', 'title']) }} />
        </ScreenReaderMessage>
      </TextButton>
    );
  }

  renderVideoButton() {
    if (this.props.ownerVideoForBib || !this.supportsUGCContentType(UGC_CONTENT_TYPES.VIDEO)) {
      return null;
    }

    return (
      <TextButton className="shelf-ugc-video-button" handleClick={() => this.openUGCEditor(UGC_CONTENT_TYPES.VIDEO)}>
        <span aria-hidden>
          <Video className="shelf-ugc-icon" />
          <FormattedMessage id="ugc_toolbar_video_btn_label" />
        </span>
        <ScreenReaderMessage>
          <FormattedMessage id="ada_add_video" values={{ title: this.props.bib.getIn(['briefInfo', 'title']) }} />
        </ScreenReaderMessage>
      </TextButton>
    );
  }

  filterOption(option) {
    if (!this.supportsUGCContentType(option)) {
      return false;
    }

    switch (option) {
      case UGC_CONTENT_TYPES.VIDEO:
        return !this.props.ownerVideoForBib;
      case UGC_CONTENT_TYPES.PRIVATE_NOTE:
        return !this.props.ownerPrivateNoteForBib;
      default:
        return true;
    }
  }

  renderAddMoreDropdown() {
    const renderAddMoreButton = () => (
      <span className="shelf-ugc-add-more-button">
        <span aria-hidden>
          <Plus className="shelf-ugc-icon" />
          <FormattedMessage id="ugc_toolbar_more_dropdown_label" />
          <Arrows.medium.Down className="selected-icon extra-small" />
        </span>
        <ScreenReaderMessage>
          <FormattedMessage id="ada_add_more_ugc" values={{ title: this.props.bib.getIn(['briefInfo', 'title']) }} />
        </ScreenReaderMessage>
      </span>
    );

    const options = [
      UGC_CONTENT_TYPES.VIDEO,
      UGC_CONTENT_TYPES.SIMILAR_TITLES,
      UGC_CONTENT_TYPES.PRIVATE_NOTE
      // UGC_CONTENT_TYPES.SUMMARY,
      // UGC_CONTENT_TYPES.AGE_SUITABILITY,
      // UGC_CONTENT_TYPES.CONTENT_ADVISORIES,
    ]
    .filter(option => this.filterOption(option))
    .map(type => ({
      value: type,
      label: (<FormattedMessage id={`ugc_${type}`} />)
    }));

    return (
      <Dropdown
        disableMobile
        className="shelf-ugc-add-more-dropdown"
        ButtonTag={TextButton}
        options={options}
        handleSelect={this.openUGCEditor}
        renderSelected={renderAddMoreButton} />
    );
  }

  renderUGCEditor() {
    switch (this.state.ugcContentType) {
      case UGC_CONTENT_TYPES.TAGS: {
        return (
          <TagsUGCEditor
            bib={this.props.bib}
            owner={this.props.owner}
            existingTags={this.props.ownerTagsForBib}
            tagsActions={this.props.tagsActions}
            tagsTransactions={this.props.tagsTransactions}
            handleClose={this.closeUGCEditor} />
        );
      }

      case UGC_CONTENT_TYPES.COMMENT: {
        return (
          <CommentUGCEditor
            bib={this.props.bib}
            shelfItem={this.props.item}
            handleClose={this.closeUGCEditor}
            commentsTransactions={this.props.commentsTransactions}
            shelfTransactions={this.props.shelfTransactions}
            commentsActions={this.props.commentsActions}
            shelfActions={this.props.shelfActions} />
        );
      }

      case UGC_CONTENT_TYPES.QUOTATIONS: {
        return (
          <QuotationUGCEditor
            bib={this.props.bib}
            shelfItem={this.props.item}
            handleClose={this.closeUGCEditor}
            quotationsTransactions={this.props.quotationsTransactions}
            shelfTransactions={this.props.shelfTransactions}
            quotationsActions={this.props.quotationsActions}
            shelfActions={this.props.shelfActions} />
        );
      }

      case UGC_CONTENT_TYPES.VIDEO: {
        return (
          <VideoUGCEditor
            bib={this.props.bib}
            shelfItem={this.props.item}
            handleClose={this.closeUGCEditor}
            videosTransactions={this.props.videosTransactions}
            shelfTransactions={this.props.shelfTransactions}
            videosActions={this.props.videosActions}
            shelfActions={this.props.shelfActions} />
        );
      }

      case UGC_CONTENT_TYPES.PRIVATE_NOTE: {
        return (
          <PrivateNoteUGCEditor
            bib={this.props.bib}
            shelfItem={this.props.item}
            handleClose={this.closeUGCEditor}
            privateNotesTransactions={this.props.privateNotesTransactions}
            shelfTransactions={this.props.shelfTransactions}
            privateNotesActions={this.props.privateNotesActions}
            shelfActions={this.props.shelfActions} />
        );
      }

      default:
        return null;
    }
  }

  renderSimilarTitlesUGCEditor() {
    if (this.state.ugcContentType === UGC_CONTENT_TYPES.SIMILAR_TITLES) {
      return (
        <SimilarTitlesUGCEditor
          bib={this.props.bib}
          shelfItem={this.props.item}
          similarTitleBibs={this.props.similarTitleBibs}
          similarTitlesTransactions={this.props.similarTitlesTransactions}
          shelfTransactions={this.props.shelfTransactions}
          similarTitlesActions={this.props.similarTitlesActions}
          shelfActions={this.props.shelfActions}
          handleClose={this.closeUGCEditor} />
      );
    }

    return null;
  }

  render() {
    return (
      <div className="cp-shelf-list-item-toolbar">
        <div className="ugc-toolbar">
          <span className="add-label"><FormattedMessage id="ugc_toolbar_add_label" /></span>
          {this.renderCommentButton()}
          {this.renderTagsButton()}
          {this.renderQuotationButton()}
          {this.renderAddMoreDropdown()}
        </div>

        <AddTitleOverlay
          open={this.state.ugcContentType === UGC_CONTENT_TYPES.SIMILAR_TITLES}
          handleClose={this.closeUGCEditor}
          handleFocusOnRestore={this.props.handleUGCFocus}>
          {this.renderSimilarTitlesUGCEditor()}
        </AddTitleOverlay>

        <FullScreenOverlay
          open={!!this.state.ugcContentType && this.state.ugcContentType !== UGC_CONTENT_TYPES.SIMILAR_TITLES}
          handleClose={this.closeUGCEditor}
          handleFocusOnRestore={this.props.handleUGCFocus}>
          {this.renderUGCEditor()}
        </FullScreenOverlay>
      </div>
    );
  }
}

ShelfListItemToolbar.propTypes = {
  bib: bibShape.isRequired,
  currentLibrary: ImmutablePropTypes.map.isRequired,
  item: shelfItemShape.isRequired,
  owner: userIdentityShape.isRequired,
  localBranch: PropTypes.string,
  ownerCommentForBib: ugcCommentShape,
  ownerTagsForBib: ImmutablePropTypes.listOf(ugcTagShape),
  ownerVideoForBib: ugcVideoShape,
  ownerPrivateNoteForBib: ugcPrivateNoteShape,
  similarTitleBibs: ImmutablePropTypes.map,
  quotationsTransactions: ImmutablePropTypes.map.isRequired,
  commentsTransactions: ImmutablePropTypes.map.isRequired,
  shelfTransactions: ImmutablePropTypes.map.isRequired,
  tagsTransactions: ImmutablePropTypes.map.isRequired,
  videosTransactions: ImmutablePropTypes.map.isRequired,
  similarTitlesTransactions: ImmutablePropTypes.map.isRequired,
  privateNotesTransactions: ImmutablePropTypes.map.isRequired,
  commentsActions: PropTypes.objectOf(PropTypes.func).isRequired,
  quotationsActions: PropTypes.objectOf(PropTypes.func).isRequired,
  shelfActions: PropTypes.objectOf(PropTypes.func).isRequired,
  tagsActions: PropTypes.objectOf(PropTypes.func).isRequired,
  videosActions: PropTypes.objectOf(PropTypes.func).isRequired,
  similarTitlesActions: PropTypes.objectOf(PropTypes.func).isRequired,
  privateNotesActions: PropTypes.objectOf(PropTypes.func).isRequired,
  handleUGCFocus: PropTypes.func.isRequired
};



// WEBPACK FOOTER //
// ./app/components/shelves/ShelfListItem/Toolbar/Toolbar.jsx